/** This modal is for viewing attachments when the user clicks on the attachment message.
 * Not to be confused with SendAttachmentModal which pops up when trying to send an attachment.
*/

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
import { useI18n } from '../../../../../context/I18nContext';
import './FileAttachmentModal.css';
import { formatFileSize, getFileType, getAttachmentIcon, getAttachmentUrl, downloadDecryptedFile } from '../../../../../utils/fileUtils';
import useFileDownload from '../../../../../hooks/useFileDownload';
import logger from '../../../../../utils/logger';
import MessageReactions from './MessageReactions';
import SmartEmojiPicker from './SmartEmojiPicker';
import ReactionDetailsModal from './ReactionDetailsModal';
import { useForwarding } from '../../../../../context/ForwardingContext';
import { useRoomContext } from '../../../../../context/RoomContext';
import { useMatrixClient } from '../../../../../context/MatrixContext';
import { canSendMessages } from '../../../../../utils/roomUtils';
import { decryptMediaBlob } from '../../../../../utils/encryptionUtils';
import { useModal } from '../../../../../context/ModalContext';
import useMalwareToast from '../../../../../hooks/useMalwareToast';

const FileAttachmentModal = ({
  message,
  senderName,
  thumbnailUrl,
  filename,
  msgtype,
  fileSize,
  mimeType,
  displayReactions,
  onReplyMessage,
  portalContainer,
  onFocusMessage,
  onToggleReaction,
  onClose,
}) => {
  const [isReactionDetailsOpen, setReactionDetailsOpen] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [textPreview, setTextPreview] = useState(null);
  const [textPreviewLoading, setTextPreviewLoading] = useState(false);
  // Malware warning toast — shown on open, auto-dismisses after 6 s.
  // Suppressed if this attachment has already been downloaded once.
  const { showMalwareToast, dismiss: dismissMalwareToast, markDownloaded } = useMalwareToast(message);
  const emojiPickerRef = useRef(null);
  const { downloadFile, downloadFileToDisplay } = useFileDownload();
  const reactions = displayReactions || message?._reactions;
  const { startForwarding } = useForwarding();
  const { room, roomId: contextRoomId, powerLevelsCounter } = useRoomContext();
  const client = useMatrixClient();
  const { showError } = useModal();
  const { t } = useI18n();

  // Check if user has permission to send messages
  const hasMessagePermission = useMemo(
    () => canSendMessages(room, client),
    [room, client, powerLevelsCounter],
  );

  const isTextFile = mimeType && mimeType.includes('text/');

  const handleDownloadClick = useCallback(async () => {
    const content = message.getContent();
    const url = getAttachmentUrl(content);
    const encryptionInfo = content.file || null;

    if (encryptionInfo) {
      try {
        // snyk:ignore:DOM-based Cross-site Scripting (XSS)
        const ok = await downloadDecryptedFile({
          downloadFileToDisplay,
          url,
          roomId: message.getRoomId(),
          mimeType: mimeType || content.info?.mimetype,
          encryptionInfo,
          filename,
        });
        if (ok) markDownloaded();
      } catch (error) {
        logger.error('Error downloading encrypted file:', error);
        showError(t('FileAttachmentModal.downloadFailed'), t('FileAttachmentModal.downloadError', { filename, error: error.message }));
      }
    } else {
      const ok = await downloadFile({ url, filename, mimeType });
      if (ok) markDownloaded();
    }
  }, [downloadFile, downloadFileToDisplay, message, filename, mimeType, showError, t]);

  const handleOpenClick = useCallback(async () => {
    const content = message.getContent();
    const url = getAttachmentUrl(content);
    const encryptionInfo = content.file || null;
    if (!url) return;

    try {
      let blobUrl = await downloadFileToDisplay({
        url,
        roomId: message.getRoomId(),
        mimeType: mimeType || content.info?.mimetype,
        skipCache: !!encryptionInfo,
      });

      if (encryptionInfo) {
        const { decryptedBlobUrl } = await decryptMediaBlob(blobUrl, encryptionInfo, mimeType);
        blobUrl = decryptedBlobUrl;
      }

      if (blobUrl) {
        // Use anchor tag click instead of window.open() for Edge compatibility
        // Edge 136+ blob URL partitioning affects window.open() but not native anchor navigation
        const link = document.createElement('a');
        link.href = blobUrl;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        link.click();
        // Cleanup after browser processes the navigation
        setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
      }
    } catch (error) {
      logger.error('Failed to open file:', error);
      showError(t('FileAttachmentModal.openFailed'), t('FileAttachmentModal.openError', { filename, error: error.message }));
    }
  }, [message, downloadFileToDisplay, mimeType, filename, showError, t]);

  const handleReplyClick = useCallback(() => {
    onClose();
    onReplyMessage(message, senderName, filename, msgtype, filename, thumbnailUrl, fileSize);
  }, [onReplyMessage, onClose, message, senderName, filename, msgtype, thumbnailUrl, fileSize]);

  const handleFocusClick = useCallback(() => {
    onClose();
    onFocusMessage(message);
  }, [onFocusMessage, onClose, message]);

  const handleForwardClick = useCallback(() => {
    const messageContent = message.getContent();
    const caption = messageContent.body !== filename ? messageContent.body : null;

    const forwardContent = {
      body: filename,
      msgtype: msgtype,
      filename: filename,
      url: getAttachmentUrl(messageContent),
      info: messageContent.info,
      caption: caption,
    };
    
    onClose();
    startForwarding(message, contextRoomId, forwardContent);
  }, [message, filename, msgtype, contextRoomId, startForwarding, onClose]);

  const handleReactClick = (e) => {
    e.stopPropagation();
    setShowEmojiPicker(prev => !prev);
  };

  // Close emoji picker when clicking outside of it
  useEffect(() => {
    if (!showEmojiPicker) return;
    const handleClickOutside = (event) => {
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(event.target)) {
        setShowEmojiPicker(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showEmojiPicker]);

  // Fetch text file preview for text/plain attachments
  useEffect(() => {
    if (!isTextFile) {
      setTextPreview(null);
      return;
    }

    let isMounted = true;
    const controller = new AbortController();

    const fetchTextContent = async () => {
      const content = message.getContent();
      const url = getAttachmentUrl(content);
      const encryptionInfo = content.file || null;
      if (!url) return;

      if (isMounted) {
        setTextPreviewLoading(true);
      }

      try {
        logger.debug(`[${filename}] Fetching text preview for modal`);
        let blobUrl = await downloadFileToDisplay({
          url,
          roomId: message.getRoomId(),
          mimeType: mimeType || content.info?.mimetype,
          signal: controller.signal,
          skipCache: !!encryptionInfo,
        });

        if (!blobUrl) {
          logger.error(`[${filename}] Failed to get blob URL for text preview`);
          if (isMounted) {
            setTextPreviewLoading(false);
          }
          return;
        }

        // Decrypt if encrypted
        if (encryptionInfo) {
          const { decryptedBlobUrl } = await decryptMediaBlob(blobUrl, encryptionInfo, mimeType);
          blobUrl = decryptedBlobUrl;
        }

        const response = await fetch(blobUrl, { signal: controller.signal });
        if (!response.ok) {
          logger.error(`[${filename}] Failed to fetch text preview: ${response.status}`);
          URL.revokeObjectURL(blobUrl);
          if (isMounted) {
            setTextPreviewLoading(false);
          }
          return;
        }

        const text = await response.text();
        logger.debug(`[${filename}] Successfully fetched text content for modal, length: ${text.length}`);

        // Revoke blob URL after we've read the text
        URL.revokeObjectURL(blobUrl);

        if (isMounted) {
          setTextPreview(text);
          setTextPreviewLoading(false);
        }
      } catch (error) {
        if (error.name === 'AbortError') {
          logger.debug(`[${filename}] Text preview fetch cancelled`);
        } else {
          logger.error(`[${filename}] Failed to fetch text preview:`, error);
        }
        if (isMounted) {
          setTextPreviewLoading(false);
        }
      }
    };

    fetchTextContent();

    return () => {
      isMounted = false;
      controller.abort();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- mimeType is safe to omit: attachments are immutable, their mimeType never changes during the modal's lifecycle
  }, [isTextFile, message, downloadFileToDisplay, filename]);

  const iconSrc = getAttachmentIcon(msgtype, filename, mimeType);
  const fileType = getFileType(mimeType, filename);
  const formattedSize = formatFileSize(fileSize);
  const isPDF = mimeType === 'application/pdf' || filename?.toLowerCase().endsWith('.pdf');

  if (!portalContainer) return null;

  return createPortal(
    <div className="file-attachment-modal-overlay" onClick={(e) => e.stopPropagation()}>
      <div className="file-attachment-modal-content" onClick={(e) => e.stopPropagation()}>
        {/* UI controls are positioned relative to the content box */}
        <button className="file-attachment-modal-close-btn" onClick={onClose} aria-label={t('FileAttachmentModal.closePreview')}>
          <i className="fa fa-times"></i>
        </button>
        <div className="file-attachment-modal-actions">
          <button className="file-attachment-modal-action-btn" title={t('FileAttachmentModal.download')} onClick={handleDownloadClick}>
            <i className="fa fa-cloud-download"></i>
          </button>
          {isPDF && (
            <button className="file-attachment-modal-action-btn" title={t('FileAttachmentModal.open')} onClick={handleOpenClick}>
              <i className="fa fa-external-link"></i>
            </button>
          )}
          {hasMessagePermission && (
            <button className="file-attachment-modal-action-btn" title={t('FileAttachmentModal.reply')} onClick={handleReplyClick}>
              <i className="fa fa-reply"></i>
            </button>
          )}
          {hasMessagePermission && (
            <button className="file-attachment-modal-action-btn" title={t('FileAttachmentModal.forward')} onClick={handleForwardClick}>
              <i className="fa fa-share"></i>
            </button>
          )}
          <button className="file-attachment-modal-action-btn" title={t('FileAttachmentModal.focus')} onClick={handleFocusClick}>
            <i className="fa fa-commenting"></i>
          </button>
          {hasMessagePermission && (
            <button className="file-attachment-modal-action-btn" title={t('FileAttachmentModal.react')} onClick={handleReactClick}>
              <img src='/images/svg/emoji-react.svg' alt='React' className="file-attachment-modal-react-icon" />
            </button>
          )}
        </div>

        {/* Main content area */}
        <div className="file-attachment-modal-details">
          {isTextFile && textPreview ? (
            <div className="file-attachment-modal-text-preview">
              <pre>{textPreview}</pre>
            </div>
          ) : isTextFile && textPreviewLoading ? (
            <div className="file-attachment-modal-loading">{t('FileAttachmentModal.loadingPreview')}</div>
          ) : (
            <>
              <img src={iconSrc} alt="File type icon" className="file-attachment-modal-icon" />
              <div className="file-attachment-modal-filename">{filename}</div>
              <div className="file-attachment-modal-file-info">{fileType}</div>
              <div className="file-attachment-modal-file-size">{formattedSize}</div>
            </>
          )}
        </div>

        {/* Reactions */}
        {reactions && reactions.size > 0 && (
          <div className="file-attachment-modal-reactions">
            <MessageReactions
              reactions={reactions}
              className="in-modal"
              onReactionDetailsOpen={() => setReactionDetailsOpen(true)}
            />
          </div>
        )}
      </div>

      {showMalwareToast && (
        <div className="file-attachment-modal-malware-toast" role="alert">
          <i className="fa fa-shield file-attachment-modal-malware-toast-icon"></i>
          <span className="file-attachment-modal-malware-toast-text">
            {t('FileAttachmentModal.malwareWarningMessage')}
          </span>
          <button
            className="file-attachment-modal-malware-toast-dismiss"
            onClick={dismissMalwareToast}
            aria-label={t('FileAttachmentModal.dismissMalwareWarning')}
          >
            <i className="fa fa-times"></i>
          </button>
        </div>
      )}

      {/* Emoji Picker Portal */}
      {showEmojiPicker && (
        <div
          ref={emojiPickerRef}
          className="file-attachment-modal-emoji-picker-container"
          onClick={(e) => e.stopPropagation()}
        >
          <SmartEmojiPicker
            messageId={message.getId()}
            reactions={reactions}
            onToggleReaction={onToggleReaction}
            onClose={() => setShowEmojiPicker(false)}
          />
        </div>
      )}

      {/* Reaction Details Modal Portal */}
      {isReactionDetailsOpen && (
        <ReactionDetailsModal
          reactions={reactions}
          onClose={() => setReactionDetailsOpen(false)}
        />
      )}
    </div>,
    portalContainer,
  );
};

FileAttachmentModal.propTypes = {
  message: PropTypes.object.isRequired,
  senderName: PropTypes.string.isRequired,
  thumbnailUrl: PropTypes.string,
  filename: PropTypes.string.isRequired,
  msgtype: PropTypes.string.isRequired,
  fileSize: PropTypes.number,
  mimeType: PropTypes.string,
  displayReactions: PropTypes.instanceOf(Map),
  portalContainer: PropTypes.object,
  onReplyMessage: PropTypes.func.isRequired,
  onFocusMessage: PropTypes.func.isRequired,
  onToggleReaction: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default FileAttachmentModal;